import { extendTheme } from "@mui/joy/styles";
import { addAlpha } from "./hexOpacity";

declare module "@mui/joy/Avatar" {
  interface AvatarPropsColorOverrides {
    primary: true;
    secondary: true;
  }
}
declare module "@mui/joy/Button" {
  interface ButtonPropsColorOverrides {
    primary: true;
    secondary: true;
    white: true;
  }

  interface ButtonPropsVariantOverrides {
    solid: true;
    soft: true;
    plain: true;
    Action: true;
    SingleSlim: true;
    White: true;
    Solid: true;
    Soft: true;
    Outlined: true;
    Plain: true;
    Selection: true;
    Tab: true;
  }

  interface ButtonPropsSizeOverrides {
    xs: true;
  }
}

declare module "@mui/joy/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    primary: true;
    soft: true;
    positive: true;
    background3: true;
  }
}
declare module "@mui/joy/CircularProgress" {
  interface CircularProgressPropsColorOverrides {
    primary: true;
    danger: true;
  }
  interface CircularProgressPropsVariantOverrides {
    solid: true;
    soft: true; // default
    Solid: true;
    Soft: true;
  }
}
declare module "@mui/joy/Typography" {
  interface TypographyPropsColorOverrides {
    positive: true;
    negative: true;
  }

  interface TypographyPropsVariantOverrides {
    H1: true;
    H2: true;
    H3: true;
    Subtitle: true;
    Body: true;
    Caption: true;
    Footnote: true;
  }
}
declare module "@mui/joy/Checkbox" {
  interface CheckboxPropsColorOverrides {
    primary: true;
  }
}

declare module "@mui/joy/styles" {
  interface ColorPalettePropOverrides {
    // apply to all Joy UI components that support `color` prop
    primary: true;
    card: true;
    text: true;
    divider: true;
    transparent: true;
    common: true;
  }

  interface TypographyVariants {
    H1: React.CSSProperties;
    H2: React.CSSProperties;
    H3: React.CSSProperties;
    Subtitle: React.CSSProperties;
    Body: React.CSSProperties;
    Caption: React.CSSProperties;
    Footnote: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    H1?: React.CSSProperties;
    H2?: React.CSSProperties;
    H3?: React.CSSProperties;
    Subtitle?: React.CSSProperties;
    Body?: React.CSSProperties;
    Caption?: React.CSSProperties;
    Footnote?: React.CSSProperties;
  }

  interface Palette {
    // this will make the node `secondary` configurable in `extendTheme`
    // and add `secondary` to the theme's palette.
    primary: {
      solid: string;
      soft: string;
      deactivated: string;
    };
    secondary: {
      solid: string;
      soft: string;
      deactivated: string;
    };
    background: {
      level1: string;
      level2: string;
      level3: string;
    };
    card: { background: string; lightStroke: string };
    text: {
      positive: string;
      negative: string;
      green: string;
      blue: string;
      orange: string;
    };
    divider: string;
    transparent: string;
    hover: string;
    hoverLight: string;
    common: { black: string; white: string };
    Background: {
      Level1: string;
      Level2: string;
      Level3: string;
      SoftHighlight: string;
    };
    Surface: {
      Highlight: string;
      SoftHighlight: string;
      Forward: string;
      Backward: string;
      Dead: string;
      HighlightWithAlert: string;
      SoftHighlightWithAlert: string;
    };
    Border: {
      Highlight: string;
      Strong: string;
      Medium: string;
      Weak: string;
      SuperWeak: string;
      HighlightWithAlert: string;
    };
    Content: {
      Highlight: string;
      SoftHighlight: string;
      Strong: string;
      Medium: string;
      Weak: string;
      Negative: string;
      SuperWeak: string;
      HighlightWithAlert: string;
    };
    System: {
      Black: string;
    };
  }
}

export const theme = extendTheme({
  // fontFamily: {
  //   display: "Roboto", // applies to `h1`–`h4`
  //   body: "Roboto", // applies to `title-*` and `body-*`
  // },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          solid: "#0F55A7",
          soft: "#DCEFFA",
          deactivated: "#DAE2E7",
        },
        secondary: {
          solid: "#4B6D8A",
          soft: "#DDE8F1",
          deactivated: "#BAC9D5",
        },
        neutral: { 100: "#F4F6F8", 200: "#E5E9F0", 300: "#D7DBE3" },
        common: {
          black: "#7F8283",
          white: "#ffffff",
        },
        background: {
          level3: "#EAEEF0",
          level2: "#EEF3F5",
          level1: "#F6F9FA",
        },
        card: {
          background: addAlpha("#FFFFFF", 0.85),
          lightStroke: "#FFFFFF",
        },
        text: {
          positive: "#1E1E1E",
          negative: "#FFFFFF",
          green: "#349434",
          blue: "#4747A6",
          orange: "#CC7611",
        },
        divider: addAlpha("#2E2B2B", 0.1),
        transparent: addAlpha("#ffffff", 0),
        hover: addAlpha("#20272B", 0.16),
        hoverLight: addAlpha("#20272B", 0.04),
        Background: {
          Level1: "#F6F9FA",
          Level2: "#EEF3F5",
          Level3: "#E6EFF2",
          SoftHighlight: "#DCEFFA",
        },
        Surface: {
          Highlight: "#0F55A7",
          SoftHighlight: "#DCEFFA",
          Forward: "#FFFFFF",
          Backward: "#E6EFF2",
          Dead: "#DAE2E7",
          HighlightWithAlert: "#F62754",
          SoftHighlightWithAlert: "#FFE3E9",
        },
        Border: {
          Highlight: "#0F55A7",
          Strong: "#20272B",
          Medium: "#BCC7D0",
          Weak: "#DAE2E7",
          SuperWeak: "#EEF3F5",
          HighlightWithAlert: "#F62754",
        },
        Content: {
          Highlight: "#0F55A7",
          SoftHighlight: "#B5DDF6",
          Strong: "#20272B",
          Medium: "#5F6162",
          Weak: "#7F8283",
          Negative: "#FFFFFF",
          SuperWeak: "#AFB6BA",
          HighlightWithAlert: "#F62754",
        },
        System: {
          Black: "#000000",
        },
      },
    },
  },
  components: {
    JoyCircularProgress: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          "--CircularProgress-trackColor": addAlpha("#20272B", 0.12),
          "--CircularProgress-progressColor":
            theme.vars.palette.Content.Highlight,
          ...((ownerState.color === "primary" ||
            ownerState.color === "danger") && {
            "--CircularProgress-trackColor": addAlpha("#000000", 0.2),
            "--CircularProgress-progressColor":
              theme.vars.palette.Content.Negative,
          }),
          ...((ownerState.variant === "Soft" && ownerState.color === "danger") && {
            "--CircularProgress-trackColor": addAlpha("#20272B", 0.12),
            "--CircularProgress-progressColor":
            theme.vars.palette.Content.HighlightWithAlert,
          }),
          ...((ownerState.variant === "Soft" && ownerState.color === "primary") && {
            "--CircularProgress-trackColor": addAlpha("#20272B", 0.12),
            "--CircularProgress-progressColor":
            theme.vars.palette.Content.Highlight,
          }),
        }),
        track: {
          strokeLinecap: "butt",
        },
        progress: {
          strokeLinecap: "butt",
        },
      },
    },
    JoyCheckbox: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        checkbox: ({ ownerState, theme }) => ({
          ...(ownerState.color === "primary" && {
            borderColor: theme.vars.palette.primary.solid,
            "&:hover": {
              backgroundColor: ownerState.checked
                ? theme.vars.palette.primary.solid
                : theme.vars.palette.primary.soft,
            },
            ...(ownerState.checked && {
              backgroundColor: theme.vars.palette.primary.solid,
              color: theme.vars.palette.text.negative,
            }),
          }),
        }),
      },
    },
    JoySvgIcon: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.color === "primary" && {
            color: theme.vars.palette.primary.solid,
          }),
          ...(ownerState.color === "soft" && {
            color: theme.vars.palette.primary.soft,
          }),
          ...(ownerState.color === "positive" && {
            color: theme.vars.palette.text.positive,
          }),
          ...(ownerState.color === "background3" && {
            color: theme.vars.palette.background.level3,
          }),
        }),
      },
    },
    JoyTypography: {
      defaultProps: {
        level: "body-sm",
        color: "positive",
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.color === "positive" && {
            color: theme.vars.palette.text.positive,
          }),
          ...(ownerState.color === "negative" && {
            color: theme.vars.palette.text.negative,
          }),
          ...(ownerState.color === "primary" && {
            color: theme.vars.palette.primary.solid,
          }),
          ...(ownerState.variant === "H1" && {
            fontSize: "32px",
            lineHeight: "126%",
          }),
          ...(ownerState.variant === "H2" && {
            fontSize: "28px",
            lineHeight: "132%",
          }),
          ...(ownerState.variant === "H3" && {
            fontSize: "22px",
            lineHeight: "132%",
          }),
          ...(ownerState.variant === "Subtitle" && {
            fontSize: "18px",
            lineHeight: "132%",
          }),
          ...(ownerState.variant === "Body" && {
            fontSize: "16px",
            lineHeight: "132%",
          }),
          ...(ownerState.variant === "Caption" && {
            fontSize: "14px",
            lineHeight: "126%",
          }),
          ...(ownerState.variant === "Footnote" && {
            fontSize: "12px",
            lineHeight: "126%",
          }),
        }),
      },
    },
    JoyAvatar: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState.color === "primary" && {
            backgroundColor: theme.vars.palette.primary.soft,
            color: theme.vars.palette.primary.solid,
          }),
          ...(ownerState.color === "secondary" && {
            backgroundColor: theme.vars.palette.secondary.solid,
            color: theme.vars.palette.text.negative,
          }),
        }),
      },
    },

    JoyListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          "@media (min-width: 1024px)": {
            "&:hover": {
              backgroundColor: theme.vars.palette.Surface.Backward,
            },
          },
          "@media (max-width: 1024px)": {
            "&:hover": {
              backgroundColor: theme.vars.palette.Surface.Backward,
            },
          },
          "&:active": {
            backgroundColor: theme.vars.palette.Surface.Backward,
          },
          "&:focus": {
            backgroundColor: theme.vars.palette.Surface.Backward,
          },
          "&.Mui-selected": {
            backgroundColor: theme.vars.palette.Surface.Backward,
            "&:hover": {
              backgroundColor: theme.vars.palette.Surface.Backward,
            },
          },
          "&:not(.Mui-selected):hover": {
            backgroundColor: theme.vars.palette.Surface.Backward,
          },
        }),
      },
    },

    JoyListItem: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&:active": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    // JoyListItemButton: {
    //   styleOverrides: {
    //     root: ({ ownerState, theme }) => ({
    //       ...(ownerState.selected && {
    //         backgroundColor: theme.vars.palette.Surface.Backward,
    //       }),
    //       "@media (min-width: 1024px)": {
    //         "&:hover": {
    //           backgroundColor: theme.vars.palette.Surface.Backward,
    //         },
    //       },
    //       "@media (max-width: 1024px)": {
    //         "&:hover": {
    //           backgroundColor: "none",
    //         },
    //       },
    //     }),
    //   },
    // },

    JoyButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderWidth: "0px",
          borderRadius: "6px",
          borderStyle: "solid",
          whiteSpace: "normal",

          ...(ownerState.size === "xs" && {
            fontSize: "14px",
            fontWeight: 700,
            paddingInline: "1rem",
            minHeight: "2rem",
          }),
          ...(ownerState.size === "sm" && {
            fontSize: "14px",
            fontWeight: 700,
            paddingInline: "12px",
            minWidth: "61px",
            minHeight: "36px",
            borderRadius: "6px",
          }),
          ...(ownerState.size === "md" && {
            fontSize: "1rem",
            fontWeight: 700,
            lineHeight: 1.32,
            paddingInline: "1rem",
            minHeight: "46px",
            borderRadius: "10px",
          }),

          ...(ownerState.size === "lg" && {
            fontSize: "1rem", // 1rem = 16px
            fontWeight: 700,
            lineHeight: 1.32,
            minHeight: "54px",
            borderRadius: "12px",
          }),

          ...(ownerState.variant === "solid" && {
            ...(ownerState.color === "primary"
              ? {
                  backgroundColor: theme.vars.palette.Surface.Highlight,
                  color: theme.vars.palette.Content.Negative,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hover}, ${theme.vars.palette.hover})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Highlight,
                      color: theme.vars.palette.Content.Negative,
                    },
                  },
                  "&:focus": {
                    backgroundColor: theme.vars.palette.Surface.Highlight,
                    color: theme.vars.palette.Content.Negative,
                  },
                  //모바일 클릭
                  "&:active": {
                    backgroundColor: theme.vars.palette.Surface.Highlight,
                    color: theme.vars.palette.Content.Negative,
                    backgroundImage: `linear-gradient(${theme.vars.palette.hover}, ${theme.vars.palette.hover})`,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Dead,
                    color: theme.vars.palette.Content.SuperWeak,
                  },
                  "&.MuiButton-loading": {
                    backgroundColor: theme.vars.palette.Surface.Highlight,
                    color: "transparent",
                  },
                }
              : ownerState.color === "secondary"
                ? {
                    backgroundColor: theme.vars.palette.secondary.solid,
                    color: theme.vars.palette.text.negative,
                    "@media (min-width: 1024px)": {
                      // 모바일에서 터치 끝나도 hover로 잡는 문제 때문에 hover은 1024 이상에서만 적용
                      //ios : 버튼 클릭 후 touchend 해도 hover
                      "&:hover": {
                        backgroundColor: theme.vars.palette.secondary.solid,
                        color: theme.vars.palette.text.negative,
                        opacity: 0.6,
                      },
                    },
                    //클릭 이후 포커스 상태
                    "&:focus": {
                      backgroundColor: theme.vars.palette.secondary.solid,
                    },
                    //모바일 클릭
                    "&:active": {
                      backgroundColor: theme.vars.palette.secondary.solid,
                      opacity: 0.6,
                    },

                    "&.Mui-disabled": {
                      backgroundColor: theme.vars.palette.Surface.Dead,
                      color: theme.vars.palette.Content.SuperWeak,
                    },
                    "&.MuiButton-loading": {
                      backgroundColor: theme.vars.palette.secondary.solid,
                      color: "transparent",
                      opacity: 0.6,
                    },
                  }
                : ownerState.color === "danger"
                  ? {
                      backgroundColor: theme.vars.palette.danger[500],
                      color: theme.vars.palette.text.negative,
                      "@media (min-width: 1024px)": {
                        // 모바일에서 터치 끝나도 hover로 잡는 문제 때문에 hover은 1024 이상에서만 적용
                        //ios : 버튼 클릭 후 touchend 해도 hover
                        "&:hover": {
                          backgroundColor: theme.vars.palette.danger[500],
                          color: theme.vars.palette.text.negative,
                          opacity: 0.6,
                        },
                      },
                      //클릭 이후 포커스 상태
                      "&:focus": {
                        backgroundColor: theme.vars.palette.danger[500],
                      },
                      //모바일 클릭
                      "&:active": {
                        backgroundColor: theme.vars.palette.danger[500],
                        opacity: 0.6,
                      },

                      "&.Mui-disabled": {
                        backgroundColor: theme.vars.palette.Surface.Dead,
                        color: theme.vars.palette.Content.SuperWeak,
                      },
                      "&.MuiButton-loading": {
                        backgroundColor: theme.vars.palette.danger[500],
                        color: "transparent",
                        opacity: 0.6,
                      },
                    }
                  : ownerState.color === "white"
                    ? {
                        backgroundColor: theme.vars.palette.common.white,
                        color: theme.vars.palette.primary.solid,
                        "@media (min-width: 1024px)": {
                          // 모바일에서 터치 끝나도 hover로 잡는 문제 때문에 hover은 1024 이상에서만 적용
                          //ios : 버튼 클릭 후 touchend 해도 hover
                          "&:hover": {
                            backgroundColor: theme.vars.palette.common.white,
                            color: theme.vars.palette.primary.solid,
                            opacity: 0.6,
                          },
                        },
                        //클릭 이후 포커스 상태
                        "&:focus": {
                          backgroundColor: theme.vars.palette.common.white,
                        },
                        //모바일 클릭
                        "&:active": {
                          backgroundColor: theme.vars.palette.common.white,
                          opacity: 0.6,
                        },

                        "&.Mui-disabled": {
                          backgroundColor: theme.vars.palette.common.white,
                          color: theme.vars.palette.primary.solid,
                        },
                        "&.MuiButton-loading": {
                          backgroundColor: theme.vars.palette.common.white,
                          color: "transparent",
                          opacity: 0.6,
                        },
                      }
                    : {}),
          }),
          ...(ownerState.variant === "outlined" && {
            ...(ownerState.color === "primary"
              ? {
                  backgroundColor: theme.vars.palette.transparent,
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Highlight}`,
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.transparent,
                    },
                  },

                  "&:active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },

                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.transparent,
                    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Medium}`,
                    color: theme.vars.palette.Content.SuperWeak,
                    pointerEvents: "none",
                  },
                }
              : {
                  backgroundColor: theme.vars.palette.transparent,
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.SuperWeak}`,
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.transparent,
                    },
                  },
                  "&:active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.transparent,
                    color: theme.vars.palette.Content.SuperWeak,
                    pointerEvents: "none",
                  },
                }),
          }),
          ...(ownerState.variant === "Soft" && {
            ...(ownerState.color === "danger"
              ? {
                  backgroundColor:
                    theme.vars.palette.Surface.SoftHighlightWithAlert,
                  color: theme.vars.palette.Content.HighlightWithAlert,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor:
                        theme.vars.palette.Surface.SoftHighlightWithAlert,
                      color: theme.vars.palette.Content.HighlightWithAlert,
                    },
                  },

                  // 포커스 상태를 기본 상태와 동일하게 유지
                  "&:focus": {
                    backgroundColor:
                      theme.vars.palette.Surface.SoftHighlightWithAlert,
                    color: theme.vars.palette.Content.HighlightWithAlert,
                  },
                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },
                  // 비활성화 상태 스타일
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Dead,
                    color: theme.vars.palette.Content.SuperWeak,
                    // 비활성화 시에는 포인터 이벤트도 비활성화하는 것이 좋습니다.
                    pointerEvents: "none",
                  },
                  // 로딩 상태 스타일
                  "&.MuiButton-loading": {
                    backgroundColor:
                      theme.vars.palette.Surface.SoftHighlightWithAlert,
                    color: "transparent",
                  },
                }
              : {
                  backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                      color: theme.vars.palette.Content.Highlight,
                    },
                  },

                  // 포커스 상태를 기본 상태와 동일하게 유지
                  "&:focus": {
                    backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                    color: theme.vars.palette.Content.Highlight,
                  },
                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },
                  // 비활성화 상태 스타일
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Dead,
                    color: theme.vars.palette.Content.SuperWeak,
                    // 비활성화 시에는 포인터 이벤트도 비활성화하는 것이 좋습니다.
                    pointerEvents: "none",
                  },
                  // 로딩 상태 스타일
                  "&.MuiButton-loading": {
                    backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                    color: "transparent",
                  },
                }),
          }),
          ...(ownerState.variant === "Selection" && {
            ...(ownerState.color === "primary"
              ? {
                  backgroundColor: theme.vars.palette.transparent,
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Highlight}`,
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.transparent,
                    },
                  },

                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },
                  // 비활성화 상태
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.transparent,
                    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Medium}`,
                    color: theme.vars.palette.Content.SuperWeak,
                    pointerEvents: "none", // 마우스 이벤트 차단
                  },
                }
              : {
                  backgroundColor: theme.vars.palette.transparent,
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.SuperWeak}`,
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.transparent,
                    },
                  },

                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },
                  // 포커스 상태를 기본 상태와 동일하게 처리
                  "&:focus": {
                    backgroundColor: theme.vars.palette.transparent,
                    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.SuperWeak}`,
                    color: theme.vars.palette.Content.Medium,
                  },
                  // 비활성화 상태
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.transparent,
                    color: theme.vars.palette.Content.SuperWeak,
                    pointerEvents: "none", // 마우스 이벤트 차단
                  },
                }),
          }),
          ...(ownerState.variant === "Outlined" && {
            backgroundColor: theme.vars.palette.transparent,
            boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Highlight}`,
            color: theme.vars.palette.Content.Highlight,
            fontWeight: 700,

            "@media (min-width: 1024px)": {
              "&:hover": {
                backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
              },
            },
            "@media (max-width: 1024px)": {
              "&:hover": {
                backgroundColor: theme.vars.palette.transparent,
              },
            },

            // 포커스 상태를 기본 상태와 동일하게 유지
            "&:focus": {
              backgroundColor: theme.vars.palette.transparent,
              boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Highlight}`,
              color: theme.vars.palette.Content.Highlight,
            },
            // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
            "&:not(.Mui-disabled):active": {
              backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
            },
            // 비활성화 상태 스타일
            "&.Mui-disabled": {
              backgroundColor: theme.vars.palette.transparent,
              boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Medium}`,
              color: theme.vars.palette.Content.SuperWeak,
              // 비활성화된 버튼에 대한 마우스 이벤트 차단
              pointerEvents: "none",
            },
            "&.MuiButton-loading": {
              backgroundColor: theme.vars.palette.transparent,
              boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Highlight}`,
              color: "transparent",
            },
          }),
          ...((ownerState.variant === "Plain" ||
            ownerState.variant === "plain") && {
            ...(ownerState.color === "neutral"
              ? {
                  backgroundColor: theme.vars.palette.Surface.Forward,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Forward,
                      color: theme.vars.palette.Content.Strong,
                    },
                  },

                  // 포커스 상태를 기본 상태와 동일하게 유지
                  "&:focus": {
                    backgroundColor: theme.vars.palette.Surface.Forward,
                    color: theme.vars.palette.Content.Strong,
                  },
                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },
                  // 비활성화 상태 스타일
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Forward,
                    color: theme.vars.palette.Content.SuperWeak,
                    // 비활성화된 버튼에 대한 마우스 이벤트 차단
                    pointerEvents: "none",
                  },
                  // 로딩 상태 스타일
                  "&.MuiButton-loading": {
                    backgroundColor: theme.vars.palette.Surface.Forward,
                    color: "transparent",
                  },
                }
              : {
                  backgroundColor: "transparent",
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.vars.palette.Content.Highlight,
                    },
                  },

                  // 포커스 상태를 기본 상태와 동일하게 유지
                  "&:focus": {
                    backgroundColor: "transparent",
                    color: theme.vars.palette.Content.Highlight,
                  },
                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                  },
                  // 비활성화 상태 스타일
                  "&.Mui-disabled": {
                    backgroundColor: "transparent",
                    color: theme.vars.palette.Content.SuperWeak,
                    // 비활성화된 버튼에 대한 마우스 이벤트 차단
                    pointerEvents: "none",
                  },
                }),
          }),
          ...(ownerState.variant === "SingleSlim" && {
            ...(ownerState.color === "primary"
              ? {
                  // Primary
                  backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Highlight}`,
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                    },
                  },

                  // 클릭(터치)하는 순간 (현재는 기본과 동일)
                  "&:not(.Mui-disabled):active": {
                    backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                  },
                  // 비활성화 상태
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Surface.Dead}`,
                    color: theme.vars.palette.Content.Highlight,
                    pointerEvents: "none", // 마우스 이벤트 차단
                  },
                }
              : {
                  // Secondary
                  backgroundColor: theme.vars.palette.Surface.Backward,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 500,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Backward,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Backward,
                      color: theme.vars.palette.Content.Strong,
                    },
                  },

                  // 클릭(터치)하는 순간 (현재는 기본과 동일)
                  "&:not(.Mui-disabled):active": {
                    backgroundColor: theme.vars.palette.Surface.Backward,
                  },
                  // 비활성화 상태
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Backward,
                    color: theme.vars.palette.Content.Strong,
                    pointerEvents: "none", // 마우스 이벤트 차단
                  },
                }),
          }),
          ...(ownerState.variant === "White" && {
            ...(ownerState.color === "primary"
              ? {
                  // Primary
                  backgroundColor: theme.vars.palette.Surface.Forward,
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Surface.Forward}`,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Forward,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Forward,
                      color: theme.vars.palette.Content.Strong,
                    },
                  },

                  // 클릭(터치)하는 순간 (현재는 기본과 동일)
                  "&:not(.Mui-disabled):active": {
                    backgroundColor: theme.vars.palette.Surface.Forward,
                  },
                  // 비활성화 상태
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Forward,
                    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Surface.Dead}`,
                    color: theme.vars.palette.Content.Strong,
                    pointerEvents: "none", // 마우스 이벤트 차단
                  },
                }
              : {
                  // Secondary
                  backgroundColor: theme.vars.palette.Surface.Backward,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 500,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Backward,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Backward,
                      color: theme.vars.palette.Content.Strong,
                    },
                  },

                  // 클릭(터치)하는 순간 (현재는 기본과 동일)
                  "&:not(.Mui-disabled):active": {
                    backgroundColor: theme.vars.palette.Surface.Backward,
                  },
                  // 비활성화 상태
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Backward,
                    color: theme.vars.palette.Content.Strong,
                    pointerEvents: "none", // 마우스 이벤트 차단
                  },
                }),
          }),
          ...((ownerState.variant === "Action" ||
            ownerState.variant === "Solid") && {
            ...(ownerState.color === "danger"
              ? {
                  backgroundColor: theme.vars.palette.Border.HighlightWithAlert,
                  color: theme.vars.palette.Content.Negative,
                  fontWeight: 700,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hover}, ${theme.vars.palette.hover})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor:
                        theme.vars.palette.Border.HighlightWithAlert,
                      color: theme.vars.palette.Content.Negative,
                    },
                  },

                  // 포커스 상태를 기본 상태와 동일하게 유지
                  "&:focus": {
                    backgroundColor:
                      theme.vars.palette.Border.HighlightWithAlert,
                    color: theme.vars.palette.Content.Negative,
                  },
                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hover}, ${theme.vars.palette.hover})`,
                  },
                  // 비활성화 상태 스타일
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Dead,
                    color: theme.vars.palette.Content.SuperWeak,
                    // 비활성화 시에는 포인터 이벤트도 비활성화하는 것이 좋습니다.
                    pointerEvents: "none",
                  },
                  // 로딩 상태 스타일
                  "&.MuiButton-loading": {
                    backgroundColor:
                      theme.vars.palette.Border.HighlightWithAlert,
                    color: "transparent",
                  },
                }
              : {
                  backgroundColor: theme.vars.palette.Surface.Highlight,
                  color: theme.vars.palette.Content.Negative,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundImage: `linear-gradient(${theme.vars.palette.hover}, ${theme.vars.palette.hover})`,
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: theme.vars.palette.Surface.Highlight,
                      color: theme.vars.palette.Content.Negative,
                    },
                  },

                  // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
                  "&:not(.Mui-disabled):active": {
                    backgroundImage: `linear-gradient(${theme.vars.palette.hover}, ${theme.vars.palette.hover})`,
                  },

                  // 비활성화 상태 스타일
                  "&.Mui-disabled": {
                    backgroundColor: theme.vars.palette.Surface.Dead,
                    color: theme.vars.palette.Content.SuperWeak,
                    // 비활성화된 버튼에 대한 모든 마우스 이벤트 차단
                    pointerEvents: "none",
                  },

                  // 로딩 상태 스타일
                  "&.MuiButton-loading": {
                    backgroundColor: theme.vars.palette.Surface.Highlight,
                    color: "transparent",
                  },
                }),
          }),
          ...(ownerState.variant === "Tab" && {
            ...(ownerState.color === "primary"
              ? {
                  // Primary
                  backgroundColor: theme.vars.palette.Surface.Forward,
                  color: theme.vars.palette.Content.Strong,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: "none",
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: "none",
                    },
                  },

                  // 클릭(터치)하는 순간 (현재는 기본과 동일)
                  "&:not(.Mui-disabled):active": {
                    backgroundColor: "none",
                  },
                }
              : {
                  // Secondary
                  backgroundColor: "transparent",
                  color: theme.vars.palette.Content.Weak,

                  "@media (min-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: "none",
                    },
                  },
                  "@media (max-width: 1024px)": {
                    "&:hover": {
                      backgroundColor: "none",
                    },
                  },

                  // 클릭(터치)하는 순간 (현재는 기본과 동일)
                  "&:not(.Mui-disabled):active": {
                    backgroundColor: "none",
                  },
                }),
          }),
        }),
      },
    },
  },
});
